import { uiText, useUiLanguage } from '@/i18n/interface';

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Shield, Users, Activity, Key, ArrowLeft } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import AdminCodeGenerator from '../components/security/AdminCodeGenerator';

const SecurityManagement = () => {
  useUiLanguage();
  const { t } = useTranslation();
  const { profile } = useAuth();
  const navigate = useNavigate();

  if (profile?.role !== 'admin') {
    return (
      <div className="wh-app min-h-screen bg-background from-red-900 via-black to-red-800 p-4">
        <div className="max-w-2xl mx-auto pt-20">
          <Card className="bg-white/95 backdrop-blur-sm shadow-2xl">
            <CardHeader className="text-center">
              <CardTitle className="text-red-900">{uiText("Access Denied")}</CardTitle>
              <CardDescription>
                {uiText("You don't have permission to access security management.")}</CardDescription>
            </CardHeader>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="wh-app min-h-screen bg-background from-red-900 via-black to-red-800 p-4">
      <div className="max-w-6xl mx-auto pt-8">
        <div className="mb-6 flex items-center gap-4">
          <Button
            variant="outline"
            onClick={() => navigate('/admin')}
            className="bg-card border-border text-foreground hover:bg-accent"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t("common:navigation.backToDashboard")}
          </Button>
          <div>
            <h1 className="wh-page-title mb-2">{t("common:navigation.securityManagement")}</h1>
            <p className="text-muted-foreground">{uiText("Manage security settings and admin access codes")}</p>
          </div>
        </div>

        <Tabs defaultValue="admin-codes" className="space-y-6">
          <TabsList className="grid w-full grid-cols-2 md:grid-cols-4 h-auto gap-1">
            <TabsTrigger value="admin-codes" className="data-[state=active]:bg-red-900 data-[state=active]:text-white">
              <Key className="h-4 w-4 mr-2" />
              {uiText("Admin Codes")}</TabsTrigger>
            <TabsTrigger value="security-log" className="data-[state=active]:bg-red-900 data-[state=active]:text-white">
              <Activity className="h-4 w-4 mr-2" />
              {uiText("Security Log")}</TabsTrigger>
            <TabsTrigger value="access-control" className="data-[state=active]:bg-red-900 data-[state=active]:text-white">
              <Shield className="h-4 w-4 mr-2" />
              {uiText("Access Control")}</TabsTrigger>
            <TabsTrigger value="user-security" className="data-[state=active]:bg-red-900 data-[state=active]:text-white">
              <Users className="h-4 w-4 mr-2" />
              {uiText("User Security")}</TabsTrigger>
          </TabsList>

          <TabsContent value="admin-codes" className="space-y-6">
            <div className="grid gap-6">
              <AdminCodeGenerator />

              <Card className="bg-white/95 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle>{uiText("Security Information")}</CardTitle>
                  <CardDescription>
                    {uiText("Important security guidelines for admin code management")}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <h4 className="font-semibold text-blue-900 mb-2">{uiText("Code Security")}</h4>
                      <ul className="text-sm text-blue-800 space-y-1">
                        <li>{uiText("• Codes are hashed and salted securely")}</li>
                        <li>{uiText("• Limited usage and expiration dates")}</li>
                        <li>{uiText("• All attempts are logged and monitored")}</li>
                      </ul>
                    </div>
                    <div className="p-4 bg-green-50 rounded-lg">
                      <h4 className="font-semibold text-green-900 mb-2">{uiText("Best Practices")}</h4>
                      <ul className="text-sm text-green-800 space-y-1">
                        <li>{uiText("• Share codes through secure channels only")}</li>
                        <li>{uiText("• Set appropriate expiration dates")}</li>
                        <li>{uiText("• Monitor code usage regularly")}</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="security-log">
            <Card className="bg-white/95 backdrop-blur-sm">
              <CardHeader>
                <CardTitle>{uiText("Security Audit Log")}</CardTitle>
                <CardDescription>
                  {uiText("Monitor security events and access attempts")}</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">{uiText("Security log viewer will be implemented in the next phase.")}</p>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="access-control">
            <Card className="bg-white/95 backdrop-blur-sm">
              <CardHeader>
                <CardTitle>{uiText("Access Control Settings")}</CardTitle>
                <CardDescription>
                  {uiText("Configure rate limiting and access policies")}</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">{uiText("Access control settings will be implemented in the next phase.")}</p>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="user-security">
            <Card className="bg-white/95 backdrop-blur-sm">
              <CardHeader>
                <CardTitle>{uiText("User Security Management")}</CardTitle>
                <CardDescription>
                  {uiText("Manage user security settings and password policies")}</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">{uiText("User security management will be implemented in the next phase.")}</p>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default SecurityManagement;
